import {
  DispatchRequestDataTable,
  HasAccess,
  PAGINATION_PAGE_SIZES,
  ResizeableContent,
  ThemedPage,
  useApp,
  usePagination
} from "@ots-frontend/ots-ui";
import {Card, Container, Group, Menu, rem, Stack, Text, Title, UnstyledButton} from "@mantine/core";
import {useCallback, useMemo} from "react";
import {
  addDays,
  adjustToTimeZone,
  dispatchRequestsSelector,
  endOfDay,
  startOfDay,
  subDays,
  useDispatchRequestPrefetch,
  useSearchDispatchRequestsQuery
} from "@ots-frontend/ots-data";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import {useNavigate} from "react-router-dom";
import {useAppSelector} from "../../store/hooks/useAppSelector";
import {getAppDttm} from "../../store/ui/selectors";
import {setAppDate} from "../../store/ui/actions";
import {DispatchRequestToolbar} from "../../components/DispatchRequestToolbar/DispatchRequestToolbar";

export type DailyDispatchRequestPageProps = {}

export const DailyDispatchRequestPage = (props: DailyDispatchRequestPageProps) => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {regionId, appCategory} = useApp();
  const {t} = useTranslation(["translation", "common"]);

  const appDttm = useAppSelector(getAppDttm);

  const prefetchDispatchRequests = useDispatchRequestPrefetch("searchDispatchRequests");

  const selectDispatchRequests = useMemo(() => {
    return dispatchRequestsSelector();
  }, []);

  const {dispatchRequests, isFetching, refetch} = useSearchDispatchRequestsQuery({
    fromDttm: adjustToTimeZone(startOfDay(appDttm)).getTime(),
    toDttm: adjustToTimeZone(endOfDay(appDttm)).getTime(),
    regionId: regionId,
    categories: appCategory ? [appCategory.code] : undefined
  }, {
    pollingInterval: 120000,
    refetchOnMountOrArgChange: 120,
    selectFromResult: (result) => ({
      ...result,
      dispatchRequests: selectDispatchRequests(result, {
        regionId: regionId,
        category: appCategory,
      }),
      isFetching: result.isFetching,
    })
  });

  const prefetchDttmRange = useCallback((date: Date) => {
    const rootFromDate = startOfDay(date);
    const rootToDate = endOfDay(date);

    // prefetch the previous day
    prefetchDispatchRequests({
      fromDttm: adjustToTimeZone(subDays(rootFromDate, 1)).getTime(),
      toDttm: adjustToTimeZone(subDays(rootToDate, 1)).getTime(),
      regionId: regionId,
      categories: appCategory ? [appCategory.code] : undefined
    });

    // prefetch the next day
    prefetchDispatchRequests({
      fromDttm: adjustToTimeZone(addDays(rootFromDate, 1)).getTime(),
      toDttm: adjustToTimeZone(addDays(rootToDate, 1)).getTime(),
      regionId: regionId,
      categories: appCategory ? [appCategory.code] : undefined
    });

  }, [prefetchDispatchRequests, regionId, appCategory]);

  const onDateChange = useCallback((date: Date) => {
    dispatch(setAppDate(date.getTime()));
    prefetchDttmRange(date);
  }, [dispatch, prefetchDttmRange]);

  const pagination = usePagination({
    data: dispatchRequests,
    recordsPerPageOptions: PAGINATION_PAGE_SIZES,
    defaultPageSize: PAGINATION_PAGE_SIZES[1]
  });

  const onNewClick = useCallback(() => {
    navigate("form");
  }, [navigate]);

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Title
                      order={4}>{t("page.dailydispatchrequests.title", "Daily Dispatch Requests", {ns: "translation"})}</Title>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t('common.action.plural', "Actions")}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <HasAccess permissions={["edit"]}>
                          <Menu.Item onClick={onNewClick}>
                            {t("actions.new", "New", {ns: "common"})}
                          </Menu.Item>
                        </HasAccess>
                        <Menu.Item onClick={() => refetch()}>
                          {t("actions.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                  <DispatchRequestToolbar
                    date={new Date(appDttm)}
                    onDateChange={onDateChange}/>
                </Card>
                <DispatchRequestDataTable
                  {...pagination}
                  withBorder
                  fetching={isFetching}/>
              </Stack>
            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
